import type { EntityTable } from "dexie";
import {
  advanceRecurring, type Command, type Membership, type SyncResponse, type Task, type User,
} from "@todoer/shared";
import { db, entityTables, getMeta, setMeta } from "./db.js";

/**
 * Sync client. Mutations are applied to IndexedDB immediately (optimistic), queued in
 * the outbox, and pushed in batches. Pulls are incremental using the server sync token.
 * A WebSocket nudge, coming back online, or the tab regaining focus all trigger a sync.
 */
type Listener = (state: SyncState) => void;
export interface SyncState { syncing: boolean; online: boolean; lastError: string | null; lastSyncAt: number | null }

const state: SyncState = { syncing: false, online: navigator.onLine, lastError: null, lastSyncAt: null };
const listeners = new Set<Listener>();
const remoteListeners = new Set<() => void>();
let inFlight: Promise<void> | null = null;
let rerun = false;
let ws: WebSocket | null = null;
let wsBackoff = 1000;
let started = false;

function emit() { for (const l of listeners) l({ ...state }); }
export function subscribe(l: Listener): () => void { listeners.add(l); l({ ...state }); return () => { listeners.delete(l); }; }
export const getSyncState = () => ({ ...state });
/** Called after every successful sync (used to refresh server-only views like activity). */
export function onRemoteChange(l: () => void): () => void { remoteListeners.add(l); return () => { remoteListeners.delete(l); }; }

/** Errors from commands the server rejected, for the UI to show. */
export const rejectedListeners = new Set<(message: string) => void>();

export function startSync(): void {
  if (started) return;
  started = true;
  connectWs();
  window.addEventListener("online", () => { state.online = true; emit(); void sync(); });
  window.addEventListener("offline", () => { state.online = false; emit(); });
  document.addEventListener("visibilitychange", () => { if (document.visibilityState === "visible") void sync(); });
  setInterval(() => void sync(), 5 * 60_000);
  void sync();
}

export function stopSync(): void {
  started = false;
  ws?.close();
  ws = null;
}

function connectWs() {
  if (!started) return;
  const proto = location.protocol === "https:" ? "wss" : "ws";
  ws = new WebSocket(`${proto}://${location.host}/api/ws`);
  ws.onopen = () => { wsBackoff = 1000; };
  ws.onmessage = (ev) => {
    try { if (JSON.parse(ev.data).type === "sync") void sync(); } catch { /* ignore */ }
  };
  ws.onclose = (ev) => {
    ws = null;
    if (ev.code === 4401 || !started) return; // not signed in; the app will restart sync after login
    setTimeout(connectWs, wsBackoff);
    wsBackoff = Math.min(wsBackoff * 2, 30_000);
  };
  ws.onerror = () => ws?.close();
}

/** Queue commands, apply them locally, and kick off a push. */
export async function dispatch(...commands: Command[]): Promise<void> {
  // applyLocal reads the signed-in user and memberships from `meta`, so it must be in scope.
  await db.transaction("rw", [...entityTables(), db.outbox, db.meta], async () => {
    for (const command of commands) {
      await applyLocal(command);
      await db.outbox.add({ command, createdAt: Date.now() });
    }
  });
  void sync();
}

export function sync(): Promise<void> {
  if (inFlight) { rerun = true; return inFlight; }
  inFlight = (async () => {
    try {
      do {
        rerun = false;
        await syncOnce();
      } while (rerun);
    } finally {
      inFlight = null;
    }
  })();
  return inFlight;
}

async function syncOnce(): Promise<void> {
  if (!navigator.onLine) return;
  state.syncing = true; emit();
  try {
    const pending = await db.outbox.orderBy("seq").limit(100).toArray();
    const syncToken = (await getMeta<string>("syncToken")) ?? "*";
    const res = await fetch("/api/sync", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ syncToken, commands: pending.map((p) => p.command) }),
    });
    if (res.status === 401) { state.lastError = "signed out"; emit(); window.dispatchEvent(new Event("todoer:unauthorised")); return; }
    if (!res.ok) throw new Error(`sync failed: ${res.status}`);
    const data = (await res.json()) as SyncResponse;
    const hadRejections = await applyRemote(data, pending.map((p) => p.seq!));
    state.lastError = null;
    state.lastSyncAt = Date.now();
    if (pending.length === 100 || hadRejections) rerun = true;
    for (const l of remoteListeners) l();
  } catch (e) {
    state.lastError = e instanceof Error ? e.message : String(e);
  } finally {
    state.syncing = false; emit();
  }
}

/** Ids touched by a command, so pending optimistic state is not overwritten by stale server rows. */
function commandIds(c: Command): string[] {
  const a = c.args as { id?: string; items?: { id: string }[] };
  return [...(a.id ? [a.id] : []), ...(a.items?.map((i) => i.id) ?? [])];
}

/** Applies a server response. Returns true when the server rejected something (a full resync is then scheduled). */
async function applyRemote(data: SyncResponse, pushedSeqs: number[]): Promise<boolean> {
  const rejected = Object.entries(data.syncStatus).filter(([, s]) => s !== "ok");
  if (rejected.length) {
    console.warn("rejected commands", rejected);
    const message = (rejected[0]![1] as { error: string }).error;
    for (const l of rejectedListeners) l(message);
  }

  await db.transaction("rw", [...entityTables(), db.outbox, db.meta], async () => {
    if (data.fullSync) await Promise.all(entityTables().map((t) => t.clear()));
    if (pushedSeqs.length) await db.outbox.bulkDelete(pushedSeqs);

    // Entities still waiting in the outbox keep their optimistic state so the UI does not flicker.
    const stillPending = new Set((await db.outbox.toArray()).flatMap((o) => commandIds(o.command)));
    const upsert = async <T extends { id: string; deletedAt: string | null }>(table: EntityTable<T, "id">, rows: T[]) => {
      const live = rows.filter((r) => !r.deletedAt && !stillPending.has(r.id));
      const gone = rows.filter((r) => r.deletedAt).map((r) => r.id);
      if (live.length) await table.bulkPut(live as Parameters<typeof table.bulkPut>[0]);
      if (gone.length) await table.bulkDelete(gone as Parameters<typeof table.bulkDelete>[0]);
    };
    await upsert(db.projects, data.projects);
    await upsert(db.sections, data.sections);
    await upsert(db.tasks, data.tasks);
    await upsert(db.labels, data.labels);
    await upsert(db.comments, data.comments ?? []);
    await upsert(db.filters, data.filters ?? []);
    await upsert(db.reminders, data.reminders ?? []);
    await upsert(db.habits, data.habits ?? []);
    await upsert(db.habitCheckins, data.habitCheckins ?? []);
    await upsert(db.focusSessions, data.focusSessions ?? []);

    // Drop anything from projects this user no longer belongs to (e.g. unshared).
    const mine = new Set((data.memberships ?? []).filter((m) => m.userId === data.user.id).map((m) => m.projectId));
    if (mine.size) {
      const stale = (await db.projects.toArray()).filter((p) => !mine.has(p.id) && !stillPending.has(p.id)).map((p) => p.id);
      if (stale.length) {
        await db.projects.bulkDelete(stale);
        await db.sections.where("projectId").anyOf(stale).delete();
        await db.tasks.where("projectId").anyOf(stale).delete();
        await db.comments.where("projectId").anyOf(stale).delete();
      }
    }

    // A rejected command leaves optimistic state the server never accepted: refetch everything.
    await setMeta("syncToken", rejected.length ? "*" : data.syncToken);
    await setMeta("user", data.user);
    await setMeta("collaborators", data.collaborators ?? []);
    await setMeta("memberships", data.memberships ?? []);
  });
  return rejected.length > 0;
}

// ---------------------------------------------------------------------------
// Optimistic local application of commands
// ---------------------------------------------------------------------------
const now = () => new Date().toISOString();

async function descendants(id: string): Promise<string[]> {
  const out: string[] = [];
  let frontier = [id];
  while (frontier.length) {
    frontier = (await db.tasks.where("parentId").anyOf(frontier).toArray()).map((t) => t.id);
    out.push(...frontier);
  }
  return out;
}

async function applyLocal(cmd: Command): Promise<void> {
  const user = await getMeta<User>("user");
  const me = user?.id ?? "me";
  const stamp = { version: 0, updatedAt: now(), deletedAt: null };
  switch (cmd.type) {
    case "project_add":
      await db.projects.put({
        ...stamp, id: cmd.args.id, name: cmd.args.name, color: cmd.args.color ?? "charcoal", parentId: cmd.args.parentId ?? null,
        childOrder: cmd.args.childOrder ?? 0, isFavorite: cmd.args.isFavorite ?? false, isArchived: false, isInbox: false,
        viewStyle: cmd.args.viewStyle ?? "list", createdBy: me,
      });
      await setMeta("memberships", [...((await getMeta<Membership[]>("memberships")) ?? []), { projectId: cmd.args.id, userId: me, role: "owner" }]);
      return;
    case "project_update": {
      const { id, ...rest } = cmd.args;
      await db.projects.update(id, { ...rest, updatedAt: now() });
      return;
    }
    case "project_reorder":
      for (const i of cmd.args.items) {
        await db.projects.update(i.id, { childOrder: i.childOrder, ...(i.parentId !== undefined ? { parentId: i.parentId } : {}) });
      }
      return;
    case "project_delete": {
      const ids = [cmd.args.id];
      for (let i = 0; i < ids.length; i++) ids.push(...(await db.projects.where("parentId").equals(ids[i]!).primaryKeys()));
      await db.tasks.where("projectId").anyOf(ids).delete();
      await db.sections.where("projectId").anyOf(ids).delete();
      await db.projects.bulkDelete(ids);
      return;
    }
    case "project_share": {
      const ms = (await getMeta<Membership[]>("memberships")) ?? [];
      if (!ms.some((m) => m.projectId === cmd.args.projectId && m.userId === cmd.args.userId)) {
        await setMeta("memberships", [...ms, { projectId: cmd.args.projectId, userId: cmd.args.userId, role: "member" }]);
      }
      return;
    }
    case "project_unshare": {
      const ms = (await getMeta<Membership[]>("memberships")) ?? [];
      await setMeta("memberships", ms.filter((m) => !(m.projectId === cmd.args.projectId && m.userId === cmd.args.userId)));
      await db.tasks.where("projectId").equals(cmd.args.projectId).modify((t) => { if (t.assigneeId === cmd.args.userId) t.assigneeId = null; });
      return;
    }
    case "project_restore":
    case "item_restore":
      return; // the restored rows arrive from the server
    case "section_add":
      await db.sections.put({ ...stamp, id: cmd.args.id, projectId: cmd.args.projectId, name: cmd.args.name, sectionOrder: cmd.args.sectionOrder ?? 0, isArchived: false });
      return;
    case "section_update": {
      const { id, ...rest } = cmd.args;
      await db.sections.update(id, { ...rest, updatedAt: now() });
      return;
    }
    case "section_delete":
      await db.tasks.where("sectionId").equals(cmd.args.id).delete();
      await db.sections.delete(cmd.args.id);
      return;
    case "item_add": {
      const a = cmd.args;
      let projectId = a.projectId;
      let sectionId = a.sectionId ?? null;
      if (a.parentId) {
        const parent = await db.tasks.get(a.parentId);
        if (parent) { projectId = parent.projectId; sectionId = parent.sectionId; }
      }
      const task: Task = {
        ...stamp, id: a.id, projectId, sectionId, parentId: a.parentId ?? null,
        content: a.content, description: a.description ?? "", priority: a.priority ?? 1, due: a.due ?? null,
        deadline: a.deadline ?? null, durationMinutes: a.durationMinutes ?? null, childOrder: a.childOrder ?? 0, dayOrder: 0,
        labelIds: a.labelIds ?? [], assigneeId: a.assigneeId ?? null, createdBy: me, completedAt: null, isCollapsed: false,
      };
      await db.tasks.put(task);
      return;
    }
    case "item_update": {
      const { id, ...rest } = cmd.args;
      await db.tasks.update(id, { ...rest, updatedAt: now() });
      return;
    }
    case "item_move": {
      const { id, ...rest } = cmd.args;
      const t = await db.tasks.get(id);
      if (!t) return;
      const patch: Partial<Task> = { ...rest, updatedAt: now() } as Partial<Task>;
      if (rest.parentId) {
        const parent = await db.tasks.get(rest.parentId);
        if (parent) { patch.projectId = parent.projectId; patch.sectionId = parent.sectionId; }
      } else if (rest.projectId && rest.projectId !== t.projectId) {
        if (rest.sectionId === undefined) patch.sectionId = null;
        if (rest.parentId === undefined) patch.parentId = null;
      }
      await db.tasks.update(id, patch);
      const kids = await descendants(id);
      if (kids.length) await db.tasks.where("id").anyOf(kids).modify({ projectId: patch.projectId ?? t.projectId, sectionId: patch.sectionId ?? t.sectionId });
      return;
    }
    case "item_reorder":
      for (const i of cmd.args.items) {
        const patch: Partial<Task> = { childOrder: i.childOrder };
        if (i.parentId !== undefined) patch.parentId = i.parentId;
        if (i.sectionId !== undefined) patch.sectionId = i.sectionId;
        await db.tasks.update(i.id, patch);
        if (i.sectionId !== undefined) {
          const kids = await descendants(i.id);
          if (kids.length) await db.tasks.where("id").anyOf(kids).modify({ sectionId: i.sectionId });
        }
      }
      return;
    case "item_complete": {
      const t = await db.tasks.get(cmd.args.id);
      if (!t) return;
      if (t.due?.isRecurring) {
        await db.tasks.update(t.id, { due: advanceRecurring(t.due, new Date()), updatedAt: now() });
      } else {
        const at = cmd.args.completedAt ?? now();
        await db.tasks.update(t.id, { completedAt: at, updatedAt: now() });
        const kids = await descendants(t.id);
        if (kids.length) await db.tasks.where("id").anyOf(kids).modify((k) => { if (!k.completedAt) k.completedAt = at; });
      }
      return;
    }
    case "item_uncomplete": {
      let t = await db.tasks.get(cmd.args.id);
      await db.tasks.update(cmd.args.id, { completedAt: null, updatedAt: now() });
      for (let i = 0; t?.parentId && i < 50; i++) {
        const parent: Task | undefined = await db.tasks.get(t.parentId);
        if (parent?.completedAt) await db.tasks.update(parent.id, { completedAt: null });
        t = parent;
      }
      return;
    }
    case "item_delete": {
      const ids = [cmd.args.id, ...(await descendants(cmd.args.id))];
      await db.tasks.bulkDelete(ids);
      await db.comments.where("taskId").anyOf(ids).delete();
      await db.reminders.where("taskId").anyOf(ids).delete();
      return;
    }
    case "label_add":
      await db.labels.put({ ...stamp, id: cmd.args.id, name: cmd.args.name, color: cmd.args.color ?? "charcoal", itemOrder: cmd.args.itemOrder ?? 0, isFavorite: cmd.args.isFavorite ?? false });
      return;
    case "label_update": {
      const { id, ...rest } = cmd.args;
      await db.labels.update(id, { ...rest, updatedAt: now() });
      return;
    }
    case "label_delete":
      await db.labels.delete(cmd.args.id);
      await db.tasks.filter((t) => t.labelIds.includes(cmd.args.id)).modify((t) => { t.labelIds = t.labelIds.filter((l) => l !== cmd.args.id); });
      return;
    case "note_add": {
      const t = await db.tasks.get(cmd.args.taskId);
      await db.comments.put({
        ...stamp, id: cmd.args.id, taskId: cmd.args.taskId, projectId: t?.projectId ?? "", userId: me,
        content: cmd.args.content, attachment: cmd.args.attachment ?? null, createdAt: now(),
      });
      return;
    }
    case "note_update":
      await db.comments.update(cmd.args.id, { content: cmd.args.content, updatedAt: now() });
      return;
    case "note_delete":
      await db.comments.delete(cmd.args.id);
      return;
    case "filter_add":
      await db.filters.put({ ...stamp, id: cmd.args.id, name: cmd.args.name, query: cmd.args.query, color: cmd.args.color ?? "charcoal", itemOrder: cmd.args.itemOrder ?? 0, isFavorite: cmd.args.isFavorite ?? false });
      return;
    case "filter_update": {
      const { id, ...rest } = cmd.args;
      await db.filters.update(id, { ...rest, updatedAt: now() });
      return;
    }
    case "filter_delete":
      await db.filters.delete(cmd.args.id);
      return;
    case "reminder_add":
      await db.reminders.put({
        ...stamp, id: cmd.args.id, taskId: cmd.args.taskId, userId: me, type: cmd.args.type,
        at: cmd.args.type === "absolute" ? cmd.args.at ?? null : null, minuteOffset: cmd.args.type === "relative" ? cmd.args.minuteOffset ?? 0 : null,
      });
      return;
    case "reminder_delete":
      await db.reminders.delete(cmd.args.id);
      return;
    case "habit_add":
      await db.habits.put({
        ...stamp, id: cmd.args.id, name: cmd.args.name, color: cmd.args.color ?? "green", icon: cmd.args.icon ?? "✅",
        targetPerWeek: cmd.args.targetPerWeek ?? 7, itemOrder: cmd.args.itemOrder ?? 0, isArchived: false, reminderTime: cmd.args.reminderTime ?? null,
      });
      return;
    case "habit_update": {
      const { id, ...rest } = cmd.args;
      await db.habits.update(id, { ...rest, updatedAt: now() });
      return;
    }
    case "habit_delete":
      await db.habits.delete(cmd.args.id);
      await db.habitCheckins.where("habitId").equals(cmd.args.id).delete();
      return;
    case "habit_checkin":
      await db.habitCheckins.where("[habitId+date]").equals([cmd.args.habitId, cmd.args.date]).delete();
      await db.habitCheckins.put({ ...stamp, id: cmd.args.id, habitId: cmd.args.habitId, date: cmd.args.date });
      return;
    case "habit_uncheck":
      await db.habitCheckins.where("[habitId+date]").equals([cmd.args.habitId, cmd.args.date]).delete();
      return;
    case "focus_add":
      await db.focusSessions.put({ ...stamp, id: cmd.args.id, taskId: cmd.args.taskId ?? null, kind: cmd.args.kind, startedAt: cmd.args.startedAt, endedAt: cmd.args.endedAt, durationSec: cmd.args.durationSec });
      return;
    case "focus_delete":
      await db.focusSessions.delete(cmd.args.id);
      return;
    case "user_update":
      if (user) await setMeta("user", { ...user, ...Object.fromEntries(Object.entries(cmd.args).filter(([, v]) => v !== undefined)) });
      return;
  }
}

export const uuid = () => crypto.randomUUID();

/** Shorthand for building a command with a fresh uuid. */
export function cmd<T extends Command["type"]>(type: T, args: Extract<Command, { type: T }>["args"]): Command {
  return { type, uuid: uuid(), args } as Command;
}
